/** Assembles the app-state half of `buildClientContext` (unit-06) from the hooks the panel can read. */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useTabs } from '@/hooks/useTabs';
import { useGameDayStatus } from '@/hooks/useGameDayStatus';
import { useTheme } from '@/app/providers/ThemeProvider';
import { getConfig } from '@/lib/config';
import { getMachineId } from '@/lib/machine-fingerprint';
import type { ClientContextInput } from '@/lib/feedback';

export interface ClientContextSource {
  build: () => ClientContextInput;
  activeSiteId: string | null;
  /** The browser fingerprint, so the server can record this workstation's settings (IC-950). Waits
   *  at most `MACHINE_ID_WAIT_MS` (the library caches it after the first call); null if it is slow
   *  or fails, and the feedback is sent without it. */
  resolveMachineId: () => Promise<string | null>;
}

export const MACHINE_ID_WAIT_MS = 1_500;

function resolveMachineId(): Promise<string | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), MACHINE_ID_WAIT_MS);
  });
  return Promise.race([getMachineId().catch(() => null), timeout]).finally(() => clearTimeout(timer));
}

export function useClientContextInput(): ClientContextSource {
  const auth = useAuth();
  const { tabs, activeTabId, activeManifest } = useTabs();
  const { theme, mode } = useTheme();
  const gameDay = useGameDayStatus();
  const queryClient = useQueryClient();

  const build = useCallback((): ClientContextInput => {
    const activeTab = tabs.find((t) => t.id === activeTabId);
    const site = auth.sites.find((s) => s.siteId === auth.activeSiteId);
    const config = getConfig();
    return {
      user: auth.user ? { displayName: auth.user.displayName, userId: auth.user.userId } : null,
      activeSite: site ? { siteId: site.siteId, name: site.name } : null,
      // Read from cache only; the panel never triggers a client-options fetch.
      clientOptions: queryClient.getQueryData(['client-options', 'current']) ?? null,
      gameDay: gameDay.gameDay
        ? { isLocked: gameDay.isLocked, gameDay: gameDay.gameDay.gameDay }
        : null,
      tab: activeTab ? { id: activeTab.id, title: activeTab.label } : null,
      tool: activeManifest ? { manifestId: activeManifest.componentKey, label: activeManifest.label } : null,
      breadcrumb: activeManifest ? { tool: activeManifest.label } : null,
      theme: String(theme),
      mode: String(mode),
      config: { environment: config.environment, version: config.version, buildTimestamp: config.buildTimestamp },
    };
  }, [auth.sites, auth.activeSiteId, auth.user, tabs, activeTabId, activeManifest, theme, mode, gameDay, queryClient]);

  return { build, activeSiteId: auth.activeSiteId, resolveMachineId };
}
